import type { Segment, Vec2 } from './types';

export const WORLD_WIDTH = 390;
export const WORLD_HEIGHT = 844;
export const MAX_AIM_RADIANS = (72 * Math.PI) / 180;
export const FIXED_LAUNCH_SPEED = 700;
export const MATH_EPSILON = 1e-9;

export interface SweepHit {
  readonly t: number;
  readonly point: Vec2;
  readonly normal: Vec2;
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function subtract(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(vector: Vec2, scalar: number): Vec2 {
  return { x: vector.x * scalar, y: vector.y * scalar };
}

export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

export function cross(a: Vec2, b: Vec2): number {
  return a.x * b.y - a.y * b.x;
}

export function lengthSquared(vector: Vec2): number {
  return dot(vector, vector);
}

export function length(vector: Vec2): number {
  return Math.sqrt(lengthSquared(vector));
}

export function distance(a: Vec2, b: Vec2): number {
  return length(subtract(a, b));
}

export function normalize(vector: Vec2, fallback: Vec2 = { x: 0, y: -1 }): Vec2 {
  const magnitude = length(vector);
  if (magnitude <= MATH_EPSILON) return fallback;
  return scale(vector, 1 / magnitude);
}

export function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };
}

/** Angle in radians, clockwise from the upward screen direction. */
export function angleFromUp(vector: Vec2): number {
  return Math.atan2(vector.x, -vector.y);
}

export function directionFromAim(angle: number, maxAim = MAX_AIM_RADIANS): Vec2 {
  const clamped = clamp(angle, -maxAim, maxAim);
  return { x: Math.sin(clamped), y: -Math.cos(clamped) };
}

/** Converts a relative pointer delta into the legal launch direction. */
export function aimFromDelta(delta: Vec2, maxAim = MAX_AIM_RADIANS): Vec2 {
  if (lengthSquared(delta) <= MATH_EPSILON) return { x: 0, y: -1 };
  return directionFromAim(angleFromUp(delta), maxAim);
}

export function aimBetween(from: Vec2, to: Vec2, maxAim = MAX_AIM_RADIANS): number {
  return clamp(angleFromUp(subtract(to, from)), -maxAim, maxAim);
}

export function fixedLaunchVelocity(direction: Vec2, speed = FIXED_LAUNCH_SPEED): Vec2 {
  return scale(normalize(direction), speed);
}

export function reflect(vector: Vec2, normal: Vec2): Vec2 {
  const unitNormal = normalize(normal, { x: 1, y: 0 });
  return subtract(vector, scale(unitNormal, 2 * dot(vector, unitNormal)));
}

export function closestPointOnSegment(point: Vec2, segment: Segment): Vec2 {
  const delta = subtract(segment.b, segment.a);
  const denominator = lengthSquared(delta);
  if (denominator <= MATH_EPSILON) return segment.a;
  const t = clamp(dot(subtract(point, segment.a), delta) / denominator, 0, 1);
  return add(segment.a, scale(delta, t));
}

export function distanceToSegment(point: Vec2, segment: Segment): number {
  return distance(point, closestPointOnSegment(point, segment));
}

/** Earliest continuous hit of a moving point against a circle. */
export function sweepPointCircle(
  start: Vec2,
  end: Vec2,
  center: Vec2,
  radius: number,
): SweepHit | null {
  const movement = subtract(end, start);
  const relative = subtract(start, center);
  const radiusSquared = radius * radius;

  if (lengthSquared(relative) <= radiusSquared + MATH_EPSILON) {
    const normal = normalize(relative, normalize(scale(movement, -1), { x: 0, y: -1 }));
    return { t: 0, point: start, normal };
  }

  const a = lengthSquared(movement);
  if (a <= MATH_EPSILON) return null;
  const b = 2 * dot(relative, movement);
  const c = lengthSquared(relative) - radiusSquared;
  const discriminant = b * b - 4 * a * c;
  if (discriminant < -MATH_EPSILON) return null;

  const root = Math.sqrt(Math.max(0, discriminant));
  const t = (-b - root) / (2 * a);
  if (t < -MATH_EPSILON || t > 1 + MATH_EPSILON) return null;
  const clampedT = clamp(t, 0, 1);
  const point = lerp(start, end, clampedT);
  return { t: clampedT, point, normal: normalize(subtract(point, center)) };
}

/**
 * Earliest continuous hit against a line segment inflated by `radius`.
 * This is a point-vs-capsule sweep and therefore also handles segment ends.
 */
export function sweepPointCapsule(
  start: Vec2,
  end: Vec2,
  segment: Segment,
  radius: number,
): SweepHit | null {
  const movement = subtract(end, start);
  const segmentDelta = subtract(segment.b, segment.a);
  const segmentLength = length(segmentDelta);

  if (segmentLength <= MATH_EPSILON) {
    return sweepPointCircle(start, end, segment.a, radius);
  }

  const startClosest = closestPointOnSegment(start, segment);
  const startOffset = subtract(start, startClosest);
  if (lengthSquared(startOffset) <= radius * radius + MATH_EPSILON) {
    const tangent = scale(segmentDelta, 1 / segmentLength);
    const fallbackNormal = normalize(scale(movement, -1), { x: -tangent.y, y: tangent.x });
    return { t: 0, point: start, normal: normalize(startOffset, fallbackNormal) };
  }

  const tangent = scale(segmentDelta, 1 / segmentLength);
  const lineNormal = { x: -tangent.y, y: tangent.x };
  const startFromA = subtract(start, segment.a);
  const signedStartDistance = dot(startFromA, lineNormal);
  const signedMovement = dot(movement, lineNormal);
  const candidates: SweepHit[] = [];

  if (Math.abs(signedMovement) > MATH_EPSILON) {
    for (const side of [-1, 1] as const) {
      const t = (side * radius - signedStartDistance) / signedMovement;
      if (t < -MATH_EPSILON || t > 1 + MATH_EPSILON) continue;
      const clampedT = clamp(t, 0, 1);
      const point = lerp(start, end, clampedT);
      const projection = dot(subtract(point, segment.a), tangent);
      if (projection < -MATH_EPSILON || projection > segmentLength + MATH_EPSILON) continue;
      const normal = scale(lineNormal, side);
      if (dot(movement, normal) < -MATH_EPSILON) {
        candidates.push({ t: clampedT, point, normal });
      }
    }
  }

  for (const endpoint of [segment.a, segment.b]) {
    const hit = sweepPointCircle(start, end, endpoint, radius);
    if (hit !== null && dot(movement, hit.normal) < -MATH_EPSILON) candidates.push(hit);
  }

  candidates.sort((left, right) => left.t - right.t);
  return candidates[0] ?? null;
}

/** Exact center-line intersection, useful for previews and editor validation. */
export function segmentIntersection(first: Segment, second: Segment): Vec2 | null {
  const firstDelta = subtract(first.b, first.a);
  const secondDelta = subtract(second.b, second.a);
  const denominator = cross(firstDelta, secondDelta);
  if (Math.abs(denominator) <= MATH_EPSILON) return null;
  const between = subtract(second.a, first.a);
  const t = cross(between, secondDelta) / denominator;
  const u = cross(between, firstDelta) / denominator;
  if (t < -MATH_EPSILON || t > 1 + MATH_EPSILON || u < -MATH_EPSILON || u > 1 + MATH_EPSILON) {
    return null;
  }
  return add(first.a, scale(firstDelta, clamp(t, 0, 1)));
}

export function hashSeed(seed: number | string): number {
  if (typeof seed === 'number') return (Math.trunc(seed) >>> 0) || 0x6d2b79f5;
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function createSeededRandom(seed: number | string): () => number {
  let state = hashSeed(seed);
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

export function seededShuffle<T>(values: readonly T[], random: () => number): T[] {
  const shuffled = [...values];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    const currentValue = shuffled[index]!;
    shuffled[index] = shuffled[other]!;
    shuffled[other] = currentValue;
  }
  return shuffled;
}
